import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useReadContract, useChainId, useSwitchChain, usePublicClient } from 'wagmi';
import { formatUnits } from 'viem';
import axios from 'axios';
import { isFixtureInUpcomingTab } from './utils/fixtureFilters';
import {
  getLeagueMenuLogoUrl,
  getLeagueMenuFallbackLogoUrl,
  LOCAL_LEAGUE_LOGOS as WC_LEAGUE_LOGOS,
} from './utils/competitionLogos';
import FixturesList from './components/FixturesList';
import GlobalClaims from './components/GlobalClaims';
import ContractInfo from './components/ContractInfo';
import themes from './themes';
import logger from './utils/logger';
import { useIsMobile } from './hooks/useIsMobile';

const MarketDetail = lazy(() => import('./components/MarketDetail'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const MarketRules = lazy(() => import('./components/MarketRules'));
const ContractsPage = lazy(() => import('./components/ContractsPage'));

function RouteFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <div className="spinner" aria-label="Loading page" />
    </div>
  );
}

const USDC_ADDRESS = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';
const USDC_ABI = [
  { name: 'balanceOf', type: 'function', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
  {
    type: 'event',
    name: 'Transfer',
    inputs: [
      { indexed: true, name: 'from', type: 'address' },
      { indexed: true, name: 'to', type: 'address' },
      { indexed: false, name: 'value', type: 'uint256' }
    ]
  }
];

/** Domestic cups nest under parent league in the dropdown; keys must match backend/oracle. */
const DOMESTIC_CUP_UNDER_PARENT = {
  facup: 'epl',
  copadelrey: 'laliga',
  dfbpokal: 'bundesliga',
  coppaitalia: 'seriea',
};
const DOMESTIC_CUP_KEYS = new Set(Object.keys(DOMESTIC_CUP_UNDER_PARENT));

/** League/competition order in the header dropdown. Unknown keys sort last (A–Z by name). */
const LEAGUE_DROPDOWN_ORDER = [
  'worldcup',
  'championsleague',
  'epl',
  'laliga',
  'bundesliga',
  'seriea',
  'ligue1',
  'championship',
  'eredivisie',
  'primeira',
  'mls',
  'ligamx',
  'championsleague',
  'europaleague',
  'conferenceleague',
  'euro',
  'copaamerica',
  'afcon',
];

/** Order of cup sub-items under each parent (same as former top-level cup order). */
const DOMESTIC_CUP_MENU_ORDER = ['facup', 'copadelrey', 'dfbpokal', 'coppaitalia'];

/**
 * Main App with Web3 wallet integration and live theme switcher
 */
function App() {
  const { address, isConnected, connector, chain } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { switchChain } = useSwitchChain();
  const [sport, setSport] = useState('all'); // League filter state
  const [fixtureStatus, setFixtureStatus] = useState('upcoming'); // Status filter: 'upcoming' or 'finished'
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false); // Dropdown state
  const [showBuyDropdown, setShowBuyDropdown] = useState(false); // Buy crypto dropdown state
  const buttonRef = useRef(null); // Reference to the league dropdown button for positioning
  const buyButtonRef = useRef(null); // Reference to the buy button for positioning
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [buyDropdownPosition, setBuyDropdownPosition] = useState({ top: 0, right: 0 });
  const lastUsdcRefetchAtRef = useRef(0);

  const walletEnv = useMemo(() => {
    if (typeof window === 'undefined') {
      return { 
        isMobileDevice: false, 
        isMetaMaskMobile: false, 
        metaMaskDeepLink: null, 
        coinbaseDeepLink: null
      };
    }
    const ua = window.navigator.userAgent || '';
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    const isMetaMask = /MetaMaskMobile/i.test(ua) || (window.ethereum?.isMetaMask && isMobile);
    const url = window.location.href;
    const normalized = url.replace(/^https?:\/\//, '');
    const encodedUrl = encodeURIComponent(url);
    return {
      isMobileDevice: isMobile,
      isMetaMaskMobile: isMetaMask,
      metaMaskDeepLink: `https://metamask.app.link/dapp/${normalized}`,
      coinbaseDeepLink: `https://go.cb-w.com/dapp?cb_url=${encodedUrl}`,
    };
  }, []);
  
  // Update dropdown position when it opens or on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (showLeagueDropdown && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8, // 8px margin below the button
          left: rect.left,
          width: rect.width,
        });
      }
    };
    
    updatePosition();
    
    if (showLeagueDropdown) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showLeagueDropdown]);
  
  // Update buy dropdown position
  useEffect(() => {
    const updateBuyPosition = () => {
      if (showBuyDropdown && buyButtonRef.current) {
        const rect = buyButtonRef.current.getBoundingClientRect();
        setBuyDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
    };
    
    updateBuyPosition();
    
    if (showBuyDropdown) {
      window.addEventListener('scroll', updateBuyPosition, true);
      window.addEventListener('resize', updateBuyPosition);
      return () => {
        window.removeEventListener('scroll', updateBuyPosition, true);
        window.removeEventListener('resize', updateBuyPosition);
      };
    }
  }, [showBuyDropdown]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close league dropdown
      if (showLeagueDropdown && 
          !event.target.closest('.league-dropdown-container') &&
          !event.target.closest('.league-dropdown-portal')) {
        setShowLeagueDropdown(false);
      }
      
      // Close buy dropdown
      if (showBuyDropdown && 
          !event.target.closest('.buy-dropdown-container') &&
          !event.target.closest('.buy-dropdown-portal')) {
        setShowBuyDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLeagueDropdown, showBuyDropdown]);
  
  // Clear old browser storage on startup to prevent quota errors (especially Firefox)
  useEffect(() => {
    const cleanupStorage = async () => {
      try {
        // 1. Clear Service Worker caches (axios, api caches)
        if (window.caches) {
          const cacheNames = await window.caches.keys();
          for (const name of cacheNames) {
            if (name.includes('axios') || name.includes('api') || name.includes('fixtures')) {
              await window.caches.delete(name);
              // Cache cleared
            }
          }
        }
        
        // 2. Clear old IndexedDB databases (except wallet-related)
        if (window.indexedDB && window.indexedDB.databases) {
          try {
            const databases = await window.indexedDB.databases();
            for (const db of databases) {
              if (db.name && !db.name.includes('wallet') && 
                  !db.name.includes('wagmi') && !db.name.includes('rainbow')) {
                // IndexedDB deleted
                window.indexedDB.deleteDatabase(db.name);
              }
            }
          } catch (e) {
            // Could not enumerate IndexedDB (normal on some browsers)
          }
        }
        
        // 3. Clear localStorage items older than 7 days (keep wallet connections)
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const keysToRemove = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && !key.includes('wallet') && !key.includes('wagmi') && 
              !key.includes('rainbow') && !key.includes('walletconnect')) {
            try {
              const item = localStorage.getItem(key);
              if (item) {
                const data = JSON.parse(item);
                if (data && data.timestamp && (now - data.timestamp) > sevenDaysMs) {
                  keysToRemove.push(key);
                }
              }
            } catch (e) {
              // Not JSON or no timestamp, consider removing if very old
              // We'll be conservative and keep it
            }
          }
        }
        
        // Remove identified keys
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          // Old localStorage cleared
        });
        
        // 4. Clear sessionStorage (safe to clear on startup)
        sessionStorage.clear();
        
        // Browser storage cleanup complete
      } catch (error) {
        console.warn('⚠️ Could not complete storage cleanup:', error);
      }
    };
    
    cleanupStorage();
  }, []);
  
  // Dynamic league configuration - fetched from backend
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  
  const LOCAL_LEAGUE_LOGOS = {
    epl: '/images/leagues/epl.png',
    laliga: '/images/leagues/laliga.png',
    bundesliga: '/images/leagues/bundesliga.png',
    seriea: '/images/leagues/seriea.png',
    mls: '/images/leagues/mls.png',
    championsleague: '/images/leagues/champions-league.png',
    europaleague: '/images/leagues/europa-league.png',
    conferenceleague: '/images/leagues/conferenceleague.png',
    worldcup: WC_LEAGUE_LOGOS.worldcup,
    euro: '/images/leagues/euro-championship.png',
    copaamerica: '/images/leagues/copa-america.png',
    afcon: '/images/leagues/africa-cup.png'
  };

  const leaguesSortedForMenu = useMemo(() => {
    const order = LEAGUE_DROPDOWN_ORDER;
    return [...availableLeagues]
      .filter((l) => !DOMESTIC_CUP_KEYS.has(l.key))
      .sort((a, b) => {
        const ia = order.indexOf(a.key);
        const ib = order.indexOf(b.key);
        const aExtra = ia === -1;
        const bExtra = ib === -1;
        if (aExtra && bExtra) return a.name.localeCompare(b.name);
        if (aExtra) return 1;
        if (bExtra) return -1;
        return ia - ib;
      });
  }, [availableLeagues]);

  const [cupKeysWithUpcoming, setCupKeysWithUpcoming] = useState(() => new Set());

  const refreshCupAvailability = useMemo(() => {
    const run = async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://3dragons.io';
      const cupKeys = availableLeagues
        .filter((l) => DOMESTIC_CUP_KEYS.has(l.key))
        .map((l) => l.key);
      if (cupKeys.length === 0) {
        setCupKeysWithUpcoming(new Set());
        return;
      }
      const next = new Set();
      await Promise.all(
        cupKeys.map(async (key) => {
          try {
            const { data } = await axios.get(
              `${BACKEND_URL}/api/fixtures/enriched?sport=${encodeURIComponent(key)}&_t=${Date.now()}`
            );
            const list = data.data || [];
            if (list.some((f) => isFixtureInUpcomingTab(f))) next.add(key);
          } catch (_) {
            /* ignore */
          }
        })
      );
      setCupKeysWithUpcoming(next);
    };
    return run;
  }, [availableLeagues]);

  useEffect(() => {
    refreshCupAvailability();
    const id = setInterval(refreshCupAvailability, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [refreshCupAvailability]);

  useEffect(() => {
    if (showLeagueDropdown) refreshCupAvailability();
  }, [showLeagueDropdown, refreshCupAvailability]);
  
  // Fetch available leagues from backend (includes auto-enabled international competitions)
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://3dragons.io';
        const response = await axios.get(`${BACKEND_URL}/api/leagues`);
        if (response.data.success) {
          setAvailableLeagues(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching leagues:', error);
        // Fallback to default leagues if API fails
        setAvailableLeagues([
          { key: 'epl', name: 'Premier League', logoId: '39', type: 'standard' },
          { key: 'laliga', name: 'La Liga', logoId: '140', type: 'standard' },
          { key: 'bundesliga', name: 'Bundesliga', logoId: '78', type: 'standard' },
          { key: 'seriea', name: 'Serie A', logoId: '135', type: 'standard' },
          { key: 'mls', name: 'MLS', logoId: '253', type: 'standard' },
        ]);
      } finally {
        setLoadingLeagues(false);
      }
    };
    
    fetchLeagues();
    // Refresh leagues every 5 minutes to catch newly auto-enabled tournaments
    const interval = setInterval(fetchLeagues, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Build league config from fetched data (for backward compatibility)
  const leagueConfig = {
    all: { label: 'All', icon: '⚽' },
    ...availableLeagues.reduce((acc, league) => {
      const logoUrl = getLeagueMenuLogoUrl(league.key, league.logoId);
      acc[league.key] = {
        label: league.name,
        logoId: parseInt(league.logoId, 10),
        type: league.type,
        logoUrl,
        fallbackLogoUrl: getLeagueMenuFallbackLogoUrl(league.key, league.logoId),
      };
      return acc;
    }, {})
  };
  
  // Read USDC balance with light polling to reduce RPC load
  const { data: usdcBalance, refetch: refetchUsdcBalance } = useReadContract({
    address: USDC_ADDRESS,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: [address],
    enabled: isConnected,
    watch: false,
    pollingInterval: 30000, // Poll every 30 seconds to avoid RPC rate limits
  });

  const triggerUsdcBalanceRefresh = useCallback(() => {
    const now = Date.now();
    // Guard against rapid duplicate events from providers/indexers.
    if (now - lastUsdcRefetchAtRef.current < 2500) {
      return;
    }
    lastUsdcRefetchAtRef.current = now;
    Promise.resolve(refetchUsdcBalance()).catch((error) => {
      logger.debug('[App] USDC balance refetch failed after transfer event:', error?.message || error);
    });
  }, [refetchUsdcBalance]);

  // Watch USDC Transfer events for this wallet (incoming + outgoing) and refetch balance immediately.
  useEffect(() => {
    if (!publicClient || !isConnected || !address) {
      return undefined;
    }

    let unwatchOutgoing;
    let unwatchIncoming;
    try {
      unwatchOutgoing = publicClient.watchContractEvent({
        address: USDC_ADDRESS,
        abi: USDC_ABI,
        eventName: 'Transfer',
        args: { from: address },
        onLogs: (logs) => {
          if (logs && logs.length > 0) {
            triggerUsdcBalanceRefresh();
          }
        },
        poll: true,
        pollingInterval: 4000
      });

      unwatchIncoming = publicClient.watchContractEvent({
        address: USDC_ADDRESS,
        abi: USDC_ABI,
        eventName: 'Transfer',
        args: { to: address },
        onLogs: (logs) => {
          if (logs && logs.length > 0) {
            triggerUsdcBalanceRefresh();
          }
        },
        poll: true,
        pollingInterval: 4000
      });
    } catch (watchError) {
      logger.warn('[App] Failed to start USDC transfer watchers:', watchError?.message || watchError);
    }

    return () => {
      if (typeof unwatchOutgoing === 'function') {
        unwatchOutgoing();
      }
      if (typeof unwatchIncoming === 'function') {
        unwatchIncoming();
      }
    };
  }, [publicClient, isConnected, address, triggerUsdcBalanceRefresh]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent
        address={address}
        isConnected={isConnected}
        connector={connector}
        chain={chain}
        chainId={chainId}
        switchChain={switchChain}
        sport={sport}
        setSport={setSport}
        fixtureStatus={fixtureStatus}
        setFixtureStatus={setFixtureStatus}
        showLeagueDropdown={showLeagueDropdown}
        setShowLeagueDropdown={setShowLeagueDropdown}
        showBuyDropdown={showBuyDropdown}
        setShowBuyDropdown={setShowBuyDropdown}
        buttonRef={buttonRef}
        buyButtonRef={buyButtonRef}
        dropdownPosition={dropdownPosition}
        setDropdownPosition={setDropdownPosition}
        buyDropdownPosition={buyDropdownPosition}
        setBuyDropdownPosition={setBuyDropdownPosition}
        loadingLeagues={loadingLeagues}
        availableLeagues={availableLeagues}
        leaguesSortedForMenu={leaguesSortedForMenu}
        leagueConfig={leagueConfig}
        cupKeysWithUpcoming={cupKeysWithUpcoming}
        usdcBalance={usdcBalance}
        refreshUsdcBalance={refetchUsdcBalance}
        walletEnv={walletEnv}
      />
    </Router>
  );
}

// Inner component that has access to router context
function AppContent({
  address,
  isConnected,
  connector,
  chain,
  chainId,
  switchChain,
  sport,
  setSport,
  fixtureStatus,
  setFixtureStatus,
  showLeagueDropdown,
  setShowLeagueDropdown,
  showBuyDropdown,
  setShowBuyDropdown,
  buttonRef,
  buyButtonRef,
  dropdownPosition,
  setDropdownPosition,
  buyDropdownPosition,
  setBuyDropdownPosition,
  loadingLeagues,
  availableLeagues,
  leaguesSortedForMenu,
  leagueConfig,
  cupKeysWithUpcoming,
  usdcBalance,
  refreshUsdcBalance,
  walletEnv,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = themes.darkLuxury.colors;
  const isMobile = useIsMobile();

  const cupsByParent = useMemo(() => {
    const map = {};
    for (const key of DOMESTIC_CUP_MENU_ORDER) {
      if (!cupKeysWithUpcoming.has(key) && sport !== key) continue;
      const row = availableLeagues.find((l) => l.key === key);
      if (!row) continue;
      const parent = DOMESTIC_CUP_UNDER_PARENT[key];
      if (!parent) continue;
      if (!map[parent]) map[parent] = [];
      map[parent].push(row);
    }
    return map;
  }, [availableLeagues, cupKeysWithUpcoming, sport]);

  const isParentLeagueOrCupSelected = (parentKey) => {
    if (sport === parentKey) return true;
    return DOMESTIC_CUP_UNDER_PARENT[sport] === parentKey;
  };
  
  // Update fixtureStatus from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlStatus = searchParams.get('status');
    if (urlStatus && (urlStatus === 'upcoming' || urlStatus === 'finished')) {
      setFixtureStatus(urlStatus);
    }
  }, [location.search, setFixtureStatus]);

  return (
    <div className="App" style={{ background: theme.bodyBg, minHeight: '100vh' }}>
      <header className="header" style={{ padding: '6px 0', background: theme.headerBg, backdropFilter: theme.glassBlur, borderBottom: theme.headerBorder, boxShadow: `0 4px 20px rgba(100, 255, 218, 0.1)`, position: 'sticky', top: 0, zIndex: 10000 }}>
        <div className="container header-inner">
            <div className="header-content" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: isMobile ? '8px' : '10px'
            }}>
              {/* 3Dragons logo */}
              <img 
                src="/dragon3-logo.png" 
                alt="3Dragons - Web3 Sports Trading" 
                width="200"
                height="100"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                className="header-logo"
                style={{ 
                  height: isMobile ? '44px' : '100px',
                  width: 'auto',
                  cursor: 'pointer',
                  borderRadius: '12px',
                  filter: 'drop-shadow(0 4px 20px rgba(100, 255, 218, 0.3))',
                  transition: 'transform 0.2s ease, border-radius 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.borderRadius = '16px';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderRadius = '12px';
                }}
                onClick={() => window.location.href = '/'}
              />

              {/* League Dropdown */}
              <div className="league-dropdown-container" style={{ 
                position: 'relative', 
                marginLeft: isMobile ? 0 : '20px', 
                marginRight: isMobile ? 0 : '20px',
                flex: isMobile ? '1 1 100%' : '0 0 auto'
              }}>
                <button
                  ref={buttonRef}
                  onClick={() => setShowLeagueDropdown(!showLeagueDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: isMobile ? '9px 12px' : '10px 16px',
                    background: 'linear-gradient(135deg, #64ffda 0%, #4fd6b8 100%)',
                    color: '#0a0e27',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(100, 255, 218, 0.3)',
                    transition: 'all 0.2s ease',
                    minWidth: isMobile ? 0 : '200px',
                    width: isMobile ? '100%' : undefined,
                    justifyContent: 'space-between'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(100, 255, 218, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(100, 255, 218, 0.3)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {loadingLeagues ? 'Loading...' : (sport === 'all' ? 'Football Leagues' : leagueConfig[sport]?.label || 'Football')}
                  </div>
                  <span style={{ fontSize: '10px' }}>{showLeagueDropdown ? '▲' : '▼'}</span>
                </button>

              </div>
              
              {/* Fixture Status Filter Toggle */}
              <div className="header-status-toggle" style={{ 
                display: 'flex', 
                gap: isMobile ? '6px' : '8px',
                alignItems: 'center',
                marginLeft: isMobile ? 0 : '10px',
                marginRight: isMobile ? 0 : 'auto'
              }}>
                <button
                  onClick={() => {
                    setFixtureStatus('upcoming');
                    // If on market detail page, navigate to list view
                    if (location.pathname.startsWith('/market/')) {
                      navigate('/?status=upcoming');
                    } else {
                      navigate('/?status=upcoming', { replace: true });
                    }
                  }}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: fixtureStatus === 'upcoming' 
                      ? 'linear-gradient(135deg, #64ffda 0%, #4fd6b8 100%)'
                      : 'rgba(100, 255, 218, 0.1)',
                    color: fixtureStatus === 'upcoming' ? '#0a0e27' : '#64ffda',
                    border: fixtureStatus === 'upcoming' ? 'none' : '1px solid rgba(100, 255, 218, 0.3)',
                    borderRadius: '12px',
                    fontSize: isMobile ? '12px' : '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: fixtureStatus === 'upcoming' ? '0 4px 20px rgba(100, 255, 218, 0.3)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (fixtureStatus !== 'upcoming') {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (fixtureStatus !== 'upcoming') {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.1)';
                    }
                  }}
                >
                  🔜 Upcoming
                </button>
                <button
                  onClick={() => {
                    setFixtureStatus('finished');
                    // If on market detail page, navigate to list view
                    if (location.pathname.startsWith('/market/')) {
                      navigate('/?status=finished');
                    } else {
                      navigate('/?status=finished', { replace: true });
                    }
                  }}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: fixtureStatus === 'finished' 
                      ? 'linear-gradient(135deg, #64ffda 0%, #4fd6b8 100%)'
                      : 'rgba(100, 255, 218, 0.1)',
                    color: fixtureStatus === 'finished' ? '#0a0e27' : '#64ffda',
                    border: fixtureStatus === 'finished' ? 'none' : '1px solid rgba(100, 255, 218, 0.3)',
                    borderRadius: '12px',
                    fontSize: isMobile ? '12px' : '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: fixtureStatus === 'finished' ? '0 4px 20px rgba(100, 255, 218, 0.3)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (fixtureStatus !== 'finished') {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (fixtureStatus !== 'finished') {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.1)';
                    }
                  }}
                >
                  ✅ Finished
                </button>
              </div>
              
              {/* Portal-based Dropdown Menu (renders outside normal DOM hierarchy) */}
              {showLeagueDropdown && ReactDOM.createPortal(
                <div 
                  className="league-dropdown-portal"
                  style={{
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: dropdownPosition.width ? `${dropdownPosition.width}px` : undefined,
                    maxWidth: 'calc(100vw - 24px)',
                    background: '#0a0e27',
                    border: '1px solid rgba(100, 255, 218, 0.3)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    zIndex: 999999,
                    minWidth: '220px',
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    animation: 'fadeIn 0.2s ease-out'
                  }}
                >
                  {/* All Leagues Option */}
                  <button
                    onClick={() => {
                      logger.debug(`[App] All Leagues clicked, current sport: ${sport}`);
                      setSport('all');
                      setShowLeagueDropdown(false);
                      logger.debug(`[App] Sport state updated to: all`);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: sport === 'all' ? 'rgba(100, 255, 218, 0.15)' : 'transparent',
                      color: sport === 'all' ? theme.primary : 'white',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: sport === 'all' ? '600' : '400',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'all 0.2s ease',
                      borderBottom: '1px solid rgba(100, 255, 218, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (sport !== 'all') e.currentTarget.style.background = 'rgba(100, 255, 218, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (sport !== 'all') e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '16px'
                    }}>
                      ⚽
                    </div>
                    <span>All Leagues</span>
                  </button>
                  
                  {/* Dynamic Leagues List — domestic cups nest under parent league when they have upcoming fixtures */}
                  {leaguesSortedForMenu.map(league => {
                    const leagueKey = league.key;
                    const config = leagueConfig[leagueKey];
                    if (!config) return null;
                    const subCups = cupsByParent[leagueKey] || [];
                    const parentRowActive = isParentLeagueOrCupSelected(leagueKey);
                    
                    return (
                      <React.Fragment key={leagueKey}>
                        <button
                          onClick={() => {
                            logger.debug(`[App] League clicked: ${leagueKey}, current sport: ${sport}`);
                            setSport(leagueKey);
                            setShowLeagueDropdown(false);
                            logger.debug(`[App] Sport state updated to: ${leagueKey}`);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: parentRowActive ? 'rgba(100, 255, 218, 0.15)' : 'transparent',
                            color: parentRowActive ? theme.primary : 'white',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: parentRowActive ? '600' : '400',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'all 0.2s ease',
                            borderBottom: subCups.length ? 'none' : '1px solid rgba(100, 255, 218, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            if (!parentRowActive) e.currentTarget.style.background = 'rgba(100, 255, 218, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            if (!parentRowActive) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {config.logoUrl && (
                            <div style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              padding: '4px',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}>
                              <img 
                                src={config.logoUrl}
                                alt={config.label}
                                width="20"
                                height="20"
                                loading="lazy"
                                decoding="async"
                                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                                onError={(e) => {
                                  if (config.fallbackLogoUrl && e.currentTarget.src !== config.fallbackLogoUrl) {
                                    e.currentTarget.src = config.fallbackLogoUrl;
                                    return;
                                  }
                                  e.currentTarget.parentElement.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <span>{config.label}</span>
                          {league.type === 'international' && (
                            <span style={{ 
                              marginLeft: 'auto', 
                              fontSize: '10px', 
                              color: '#64ffda',
                              opacity: 0.7
                            }}>
                              🏆
                            </span>
                          )}
                        </button>
                        {subCups.map((cup) => {
                          const cupConfig = leagueConfig[cup.key];
                          if (!cupConfig) return null;
                          const cupActive = sport === cup.key;
                          return (
                            <button
                              key={cup.key}
                              type="button"
                              onClick={() => {
                                setSport(cup.key);
                                setShowLeagueDropdown(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 16px 10px 36px',
                                background: cupActive ? 'rgba(100, 255, 218, 0.12)' : 'transparent',
                                color: cupActive ? theme.primary : 'rgba(255,255,255,0.92)',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: cupActive ? '600' : '400',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                borderBottom: '1px solid rgba(100, 255, 218, 0.08)'
                              }}
                              onMouseEnter={(e) => {
                                if (!cupActive) e.currentTarget.style.background = 'rgba(100, 255, 218, 0.08)';
                              }}
                              onMouseLeave={(e) => {
                                if (!cupActive) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              {cupConfig.logoUrl && (
                                <div style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '50%',
                                  background: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  padding: '3px',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}>
                                  <img 
                                    src={cupConfig.logoUrl}
                                    alt={cupConfig.label}
                                    width="16"
                                    height="16"
                                    loading="lazy"
                                    decoding="async"
                                    style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                                    onError={(e) => {
                                      if (cupConfig.fallbackLogoUrl && e.currentTarget.src !== cupConfig.fallbackLogoUrl) {
                                        e.currentTarget.src = cupConfig.fallbackLogoUrl;
                                        return;
                                      }
                                      e.currentTarget.parentElement.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              <span>{cupConfig.label}</span>
                            </button>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* Show message if no leagues loaded */}
                  {!loadingLeagues && availableLeagues.length === 0 && (
                    <div style={{
                      padding: '12px 16px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '12px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      No leagues available
                    </div>
                  )}
                </div>,
                document.body
              )}

            <ConnectButton.Custom>
              {({
                account,
                chain,
                openAccountModal,
                openChainModal,
                openConnectModal,
                mounted,
              }) => {
                const ready = mounted;
                const connected = ready && account && chain;

                return (
                  <div
                    className="header-wallet"
                    {...(!ready && {
                      'aria-hidden': true,
                      'style': {
                        opacity: 0,
                        pointerEvents: 'none',
                        userSelect: 'none',
                      },
                    })}
                  >
                    {(() => {
                      if (!connected) {
                        return (
                          <div className="header-wallet-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button 
                              onClick={openConnectModal} 
                              type="button"
                              style={{
                                padding: '8px 16px',
                                background: theme.buttonBg,
                                color: theme.buttonText,
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: theme.buttonShadow
                              }}
                            >
                              Connect Wallet
                            </button>
                            {walletEnv.isMobileDevice && !walletEnv.isMetaMaskMobile && (
                              <>
                                {walletEnv.metaMaskDeepLink && (
                                  <button
                                    onClick={() => {
                                      window.location.href = walletEnv.metaMaskDeepLink;
                                    }}
                                    type="button"
                                    style={{
                                      padding: '8px 10px',
                                      background: theme.glassBg,
                                      color: theme.textPrimary,
                                      border: theme.glassBorder,
                                      borderRadius: '12px',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}>
                                      <img
                                        src="/images/wallets/metamask.svg"
                                        alt="MetaMask"
                                        width="16"
                                        height="16"
                                        loading="lazy"
                                        decoding="async"
                                        style={{ display: 'inline-block' }}
                                      />
                                      Open in MetaMask
                                    </span>
                                  </button>
                                )}
                                {walletEnv.coinbaseDeepLink && (
                                  <button
                                    onClick={() => {
                                      window.location.href = walletEnv.coinbaseDeepLink;
                                    }}
                                    type="button"
                                    style={{
                                      padding: '8px 10px',
                                      background: theme.glassBg,
                                      color: theme.textPrimary,
                                      border: theme.glassBorder,
                                      borderRadius: '12px',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}>
                                      <img
                                        src="/images/wallets/coinbase.svg"
                                        alt="Coinbase Wallet"
                                        width="16"
                                        height="16"
                                        loading="lazy"
                                        decoding="async"
                                        style={{ display: 'inline-block' }}
                                      />
                                      Open in Coinbase
                                    </span>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        );
                      }

                      if (chain.unsupported) {
                        return (
                          <button 
                            onClick={() => {
                              // Try to switch to Polygon automatically
                              if (switchChain) {
                                try {
                                  switchChain({ chainId: 137 }); // Polygon mainnet
                                } catch (e) {
                                  // Fallback to modal if auto-switch fails
                                  openChainModal();
                                }
                              } else {
                                openChainModal();
                              }
                            }}
                            type="button"
                            style={{
                              padding: '8px 16px',
                              background: theme.danger,
                              color: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            Switch to Polygon
                          </button>
                        );
                      }

                      return (
                        <div className="header-wallet-actions" style={{ display: 'flex', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap', alignItems: 'stretch' }}>
                          {/* Chain Selector Button */}
                          <button
                            className="header-chain-btn"
                            onClick={openChainModal}
                            type="button"
                            style={{
                              padding: isMobile ? '8px 10px' : '8px 12px',
                              background: theme.glassBg,
                              border: theme.glassBorder,
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: theme.textPrimary,
                              backdropFilter: theme.glassBlur,
                              flex: '0 0 auto'
                            }}
                          >
                            {chain.hasIcon && (
                              <div
                                style={{
                                  background: chain.iconBackground,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {chain.iconUrl && (
                                  <img
                                    alt={chain.name ?? 'Chain icon'}
                                    src={chain.iconUrl}
                                    width="20"
                                    height="20"
                                    loading="lazy"
                                    decoding="async"
                                    style={{ width: 20, height: 20 }}
                                  />
                                )}
                              </div>
                            )}
                            <span className="header-chain-name">{chain.name}</span>
                          </button>

                          {/* Account Button with Wallet Logo and Balances */}
                          <button
                            className="header-account-btn"
                            onClick={openAccountModal}
                            type="button"
                            style={{
                              padding: isMobile ? '6px 10px' : '8px 12px',
                              background: theme.glassBg,
                              border: theme.glassBorder,
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              fontSize: isMobile ? '12px' : '14px',
                              fontWeight: '600',
                              color: theme.textPrimary,
                              backdropFilter: theme.glassBlur,
                              minWidth: 0,
                              flex: '1 1 auto'
                            }}
                          >
                            {/* Wallet Logo */}
                            {connector?.icon && (
                              <img
                                src={connector.icon}
                                alt={connector.name || 'Wallet'}
                                width="20"
                                height="20"
                                loading="lazy"
                                decoding="async"
                                style={{ width: 20, height: 20, borderRadius: '4px' }}
                              />
                            )}
                            
                            <span className="header-account-balances">
                            {usdcBalance !== undefined && (
                              <span style={{ color: theme.primary }}>{parseFloat(formatUnits(usdcBalance, 6)).toFixed(2)} USDC</span>
                            )}
                            <span>{account.displayBalance}</span>
                            </span>
                          </button>
                          
                          {/* Buy Crypto Button */}
                          <div className="buy-dropdown-container">
                            <button
                              ref={buyButtonRef}
                              onClick={() => setShowBuyDropdown(!showBuyDropdown)}
                              type="button"
                              style={{
                                padding: isMobile ? '8px 12px' : '8px 16px',
                                background: 'linear-gradient(135deg, #00d4aa 0%, #00a478 100%)',
                                border: 'none',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: '600',
                                color: 'white',
                                boxShadow: '0 4px 15px rgba(0, 212, 170, 0.3)',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                flex: '0 0 auto'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 212, 170, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 212, 170, 0.3)';
                              }}
                            >
                              💳 <span className="header-buy-label-full">Buy Crypto</span><span className="header-buy-label-short">Buy</span>
                            </button>
                          </div>
                          
                          {/* Buy Dropdown Menu - Portal */}
                          {showBuyDropdown && ReactDOM.createPortal(
                            <div
                              className="buy-dropdown-portal"
                              style={{
                                position: 'fixed',
                                top: `${buyDropdownPosition.top}px`,
                                right: `${buyDropdownPosition.right}px`,
                                background: theme.glassBg,
                                border: theme.glassBorder,
                                borderRadius: '12px',
                                padding: '8px',
                                minWidth: '200px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                backdropFilter: theme.glassBlur,
                                zIndex: 99999
                              }}
                            >
                                <button
                                  onClick={() => {
                                    window.open(`https://portfolio.metamask.io/buy?chainId=137&token=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359`, '_blank');
                                    setShowBuyDropdown(false);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: theme.textPrimary,
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  {/* USDC Icon */}
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="12" fill="#2775CA"/>
                                    <path d="M15.3 14.1c0-1.8-1.2-2.7-3.6-3-.9-.15-1.2-.45-1.2-.9 0-.45.3-.75 1.05-.75.75 0 1.2.3 1.35.9h2.25c-.15-1.5-1.35-2.55-3.15-2.7V6h-2.1v1.65c-1.65.15-2.85 1.2-2.85 2.7 0 1.65 1.2 2.55 3.45 2.85.9.15 1.35.45 1.35.9 0 .45-.45.75-1.2.75-.9 0-1.35-.3-1.5-.9H6.9c.15 1.65 1.35 2.7 3.15 2.85V18h2.1v-1.65c1.65-.15 3-.9 3-2.25zm-3.15 6.15c-4.65 0-8.4-3.75-8.4-8.4 0-4.65 3.75-8.4 8.4-8.4 4.65 0 8.4 3.75 8.4 8.4 0 4.65-3.75 8.4-8.4 8.4z" fill="white"/>
                                  </svg>
                                  <div>
                                    <div>Buy USDC</div>
                                    <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '400' }}>For trading</div>
                                  </div>
                                </button>
                                
                                <button
                                  onClick={() => {
                                    window.open(`https://portfolio.metamask.io/buy?chainId=137`, '_blank');
                                    setShowBuyDropdown(false);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: theme.textPrimary,
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  {/* Polygon (POL) Icon */}
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z" fill="#8247E5"/>
                                    <path d="M15.5 9.5c-.3-.2-.7-.2-1 0L12 11.4l-1.5-.9-1.5-.9c-.3-.2-.7-.2-1 0l-2 1.2c-.3.2-.5.5-.5.9v2.4c0 .4.2.7.5.9l2 1.2c.3.2.7.2 1 0l1.5-.9 1.5-.9c.3-.2.7-.2 1 0l1.5.9 1.5.9c.3.2.7.2 1 0l2-1.2c.3-.2.5-.5.5-.9v-2.4c0-.4-.2-.7-.5-.9l-2-1.2-.5-.3z" fill="white"/>
                                  </svg>
                                  <div>
                                    <div>Buy POL</div>
                                    <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '400' }}>For gas fees</div>
                                  </div>
                                </button>
                            </div>,
                            document.body
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              }}
            </ConnectButton.Custom>
          </div>
        </div>
      </header>
      
      <main className="container" style={{ paddingTop: '10px' }}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <FixturesList
                    sport={sport}
                    leagueLabel={sport === 'all' ? null : leagueConfig[sport]?.label}
                    fixtureStatus={fixtureStatus}
                  />
                }
              />
              <Route path="/market/:marketAddress" element={<MarketDetail />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/rules" element={<MarketRules />} />
              <Route path="/contracts" element={<ContractsPage />} />
            </Routes>
          </Suspense>
        </main>

      <footer style={{
        marginTop: '40px',
        padding: '20px',
        textAlign: 'center',
        color: theme.textSecondary,
        borderTop: `1px solid ${theme.primary}33`
      }}>
        <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: theme.textPrimary }}>
          Parimutuel prediction markets on sporting events
        </p>
        
        {/* Legal + contact */}
        <div style={{ fontSize: '12px', marginBottom: '16px' }}>
          <Link to="/terms" style={{ color: 'white', textDecoration: 'none', margin: '0 10px', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'white'; }}>Terms of Service</Link>
          <span style={{ color: 'white', opacity: 0.5 }}>•</span>
          <Link to="/privacy" style={{ color: 'white', textDecoration: 'none', margin: '0 10px', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'white'; }}>Privacy Policy</Link>
          <span style={{ color: 'white', opacity: 0.5 }}>•</span>
          <Link to="/rules" style={{ color: 'white', textDecoration: 'none', margin: '0 10px', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'white'; }}>Market Rules</Link>
          <span style={{ color: 'white', opacity: 0.5 }}>•</span>
          <Link to="/contracts" style={{ color: 'white', textDecoration: 'none', margin: '0 10px', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'white'; }}>Smart Contracts</Link>
        </div>

        <div style={{ marginBottom: '16px', fontSize: '14px' }}>
          <a
            href="mailto:admin@3dragons.io"
            style={{
              color: theme.textSecondary,
              textDecoration: 'none',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.textSecondary; }}
          >
            admin@3dragons.io
          </a>
        </div>
        
        {/* Contract Verification Links - Compact */}
        <div style={{ margin: '24px 0 16px 0', padding: '16px 0', borderTop: `1px solid ${theme.primary}22`, borderBottom: `1px solid ${theme.primary}22` }}>
          <ContractInfo theme={theme} compact={true} />
        </div>
        
        <p style={{ margin: '10px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
          © 2026 3Dragons. Decentralized • Trustless • Community Driven
        </p>
      </footer>
      
      {/* Global Claims (bottom-left, for all users with winnings) */}
      <GlobalClaims onClaimSuccess={refreshUsdcBalance} />
      
      {/* Claim Earnings (bottom-right, only visible to frontend owner) */}
    </div>
  );
}

export default App;

